import { InjectionToken } from '@angular/core';
import { environment } from 'src/environments/environment';
import type { Country } from './country';
import { type Location, locations, topCities } from './data/se-locations';

export type { Country } from './country';

// Features that are SE-only in v1 and gated off in new markets (FR7). Star ratings
// and local-language product body content are always on, so they are not gateable.
export type FeatureFlag =
  | 'stockAlerts'
  | 'priceAlerts'
  | 'priceHistory'
  | 'ingredientWidget'
  | 'reviews'
  | 'drugRegister'
  | 'drugShortages'
  | 'fassLink'
  | 'doseCalculator'
  | 'inStoreAvailability'
  | 'ragSearch'
  | 'barcodeScan'
  | 'editorialGuides'
  | 'editorialArticles';

export type { Location } from './data/se-locations';

export interface InStoreConfig {
  readonly stockVendors: readonly string[];
  readonly topCities: readonly Location[];
  readonly locations: readonly Location[];
}

export interface FooterLink {
  readonly label: string;
  readonly path: string;
}

// A footer link to a sibling market's site. Distinct from FooterLink because the
// target is an absolute URL on another origin: it binds [href], never
// [routerLink], and carries that market's flag asset.
export interface MarketSwitchLink {
  readonly label: string;
  readonly href: string;
  readonly flagSrc: string;
}

export interface FooterConfig {
  readonly variant: 'full' | 'limited';
  // Market-local legal pages in the limited footer; becomes CMS article links once legal moves to Directus
  readonly legalLinks: readonly FooterLink[];
}

export interface SiteSchemaConfig {
  readonly organizationName: string;
  readonly areaServed: string;
  readonly sameAs: readonly string[];
}

/**
 * Which consent banner a market runs. 'self-hosted' markets carry no
 * third-party script at all and gate everything behind ConsentService;
 * 'cookieyes' markets would carry the CookieYes script and their analytics
 * tags statically in index.html. Every market is self-hosted since SE and DK
 * moved off CookieYes; the mode stays so a market can opt back into a hosted
 * CMP without touching ConsentService. Deliberately not a FeatureFlag: it
 * cuts across the ALL_ON / ALL_OFF feature maps.
 */
export type ConsentMode = 'cookieyes' | 'self-hosted';

export interface CountryConfig {
  readonly locale: string;
  readonly currency: string;
  readonly timezone: string;
  readonly countryName: string;
  readonly productTable: string;
  readonly cacheTable: string;
  readonly typesenseCollection: string;
  readonly carouselView: string;
  readonly host: string;
  // Market-localized URL path for the campaigns page, without a leading slash.
  // Each market serves this page on its own language's slug; SE keeps the
  // originally-indexed Swedish path. Single source of truth for the route, the
  // nav links and the canonical tag.
  readonly campaignsPath: string;
  readonly consentMode: ConsentMode;
  readonly features: Readonly<Record<FeatureFlag, boolean>>;
  readonly footer: FooterConfig;
  readonly siteSchema: SiteSchemaConfig;
  // Market has editor-authored DB meta prose (catdb.metadesc, tags.title_tag/metadesc).
  // SE-only until #32 country-scopes that content; gates curated-vs-generated meta on
  // capability, not on a source-locale (isSEMarket) check. Brand meta is exempt:
  // brandb.translations already resolves per country, so it needs no market gate.
  readonly curatedMeta: boolean;
  // Editorial content catalogs, country-scoped: SE-populated, empty elsewhere until
  // each market's content lands (lights up with no feature gate to remove -- #32).
  readonly featuredArticleSlugs: readonly string[];
  readonly priceGuideSlugs: readonly string[];
  readonly inStore?: InStoreConfig;
}

const ALL_ON: Readonly<Record<FeatureFlag, boolean>> = {
  stockAlerts: true,
  priceAlerts: true,
  priceHistory: true,
  ingredientWidget: true,
  reviews: true,
  drugRegister: true,
  drugShortages: true,
  fassLink: true,
  doseCalculator: true,
  inStoreAvailability: true,
  ragSearch: true,
  barcodeScan: true,
  editorialGuides: true,
  editorialArticles: true,
};

const ALL_OFF: Readonly<Record<FeatureFlag, boolean>> = {
  stockAlerts: false,
  priceAlerts: false,
  priceHistory: false,
  ingredientWidget: false,
  reviews: false,
  drugRegister: false,
  drugShortages: false,
  fassLink: false,
  doseCalculator: false,
  inStoreAvailability: false,
  ragSearch: false,
  barcodeScan: false,
  editorialGuides: false,
  editorialArticles: false,
};

// The eight legal articles each EU market has in the CMS, in its own language and
// under its own slug. Single source of truth: legalArticleGuard reads it to decide
// which /article/:article slugs resolve off SE, and each market's footer.legalLinks
// is derived from it below, so a slug and its label are never written twice.
// SE is absent on purpose: it resolves every article slug and keeps the full footer.
// Two of the eight, the identity and disclaimer pair, are carried by every market
// but linked only where LEGAL_FOOTER_GATE_OPEN says the content exists.
interface LegalArticle {
  readonly slug: string;
  readonly label: string;
  // Marks the two articles other code has to address by role rather than by name:
  // the contact page the contact form is rendered on, and the privacy policy that
  // form links to. Marking them beats indexing into the list, which would silently
  // point at the wrong article the first time a market's entries are reordered.
  readonly role?: ArticleRole;
  // Marks the identity and disclaimer pair. Their slugs are configured on every
  // market so the pages resolve by direct URL, but legalLinksFor withholds the
  // footer links until legalFooterGateOpen says the market's articles exist.
  readonly footerGated?: boolean;
}

export type ArticleRole = 'privacy' | 'contact';

const LEGAL_ARTICLES: Readonly<Record<Exclude<Country, 'SE'>, readonly LegalArticle[]>> = {
  DK: [
    { slug: 'privatlivspolitik', label: 'Privatlivspolitik', role: 'privacy' },
    { slug: 'cookiepolitik', label: 'Cookiepolitik' },
    { slug: 'brugervilkar', label: 'Brugervilkår' },
    { slug: 'virksomhedsoplysninger', label: 'Virksomhedsoplysninger', footerGated: true },
    { slug: 'ansvarsfraskrivelse', label: 'Ansvarsfraskrivelse', footerGated: true },
    { slug: 'kontakt-os', label: 'Kontakt os', role: 'contact' },
    { slug: 'om-os', label: 'Om Aposmart' },
    { slug: 'rapporter-fejl', label: 'Rapportér fejl' },
  ],
  NO: [
    { slug: 'personvernerklaering', label: 'Personvernerklæring', role: 'privacy' },
    { slug: 'informasjonskapsler', label: 'Informasjonskapsler' },
    { slug: 'brukervilkar', label: 'Brukervilkår' },
    { slug: 'selskapsinformasjon', label: 'Selskapsinformasjon', footerGated: true },
    { slug: 'ansvarsfraskrivelse', label: 'Ansvarsfraskrivelse', footerGated: true },
    { slug: 'kontakt-oss', label: 'Kontakt oss', role: 'contact' },
    { slug: 'om-oss', label: 'Om Aposmart' },
    { slug: 'rapporter-feil', label: 'Rapporter feil' },
  ],
  DE: [
    { slug: 'datenschutzerklaerung', label: 'Datenschutzerklärung', role: 'privacy' },
    { slug: 'cookie-richtlinie', label: 'Cookie-Richtlinie' },
    { slug: 'nutzungsbedingungen', label: 'Nutzungsbedingungen' },
    { slug: 'impressum', label: 'Impressum', footerGated: true },
    { slug: 'haftungsausschluss', label: 'Haftungsausschluss', footerGated: true },
    { slug: 'kontakt', label: 'Kontakt', role: 'contact' },
    { slug: 'ueber-aposmart', label: 'Über Aposmart' },
    { slug: 'fehler-melden', label: 'Fehler melden' },
  ],
  GB: [
    { slug: 'privacy-policy', label: 'Privacy Policy', role: 'privacy' },
    { slug: 'cookie-policy', label: 'Cookie Policy' },
    { slug: 'terms-of-use', label: 'Terms of Use' },
    { slug: 'legal-notice', label: 'Legal Notice', footerGated: true },
    { slug: 'disclaimer', label: 'Disclaimer', footerGated: true },
    { slug: 'contact-us', label: 'Contact Us', role: 'contact' },
    { slug: 'about-aposmart', label: 'About Aposmart' },
    { slug: 'report-an-error', label: 'Report an Error' },
  ],
  EE: [
    { slug: 'privaatsuspoliitika', label: 'Privaatsuspoliitika', role: 'privacy' },
    { slug: 'kupsisepoliitika', label: 'Küpsisepoliitika' },
    { slug: 'kasutustingimused', label: 'Kasutustingimused' },
    { slug: 'ettevotte-andmed', label: 'Ettevõtte andmed', footerGated: true },
    { slug: 'vastutusest-loobumine', label: 'Vastutusest loobumine', footerGated: true },
    { slug: 'vota-uhendust', label: 'Kontakt', role: 'contact' },
    { slug: 'aposmartist', label: 'Aposmartist' },
    { slug: 'teata-veast', label: 'Teata veast' },
  ],
  FI: [
    { slug: 'tietosuojaseloste', label: 'Tietosuojaseloste', role: 'privacy' },
    { slug: 'evastekaytanto', label: 'Evästekäytäntö' },
    { slug: 'kayttoehdot', label: 'Käyttöehdot' },
    { slug: 'yritystiedot', label: 'Yritystiedot', footerGated: true },
    { slug: 'vastuuvapauslauseke', label: 'Vastuuvapauslauseke', footerGated: true },
    { slug: 'yhteystiedot', label: 'Yhteystiedot', role: 'contact' },
    { slug: 'tietoa-aposmartista', label: 'Tietoa Aposmartista' },
    { slug: 'ilmoita-virheesta', label: 'Ilmoita virheestä' },
  ],
  LT: [
    { slug: 'privatumo-politika', label: 'Privatumo politika', role: 'privacy' },
    { slug: 'slapuku-politika', label: 'Slapukų politika' },
    { slug: 'naudojimosi-salygos', label: 'Naudojimosi sąlygos' },
    { slug: 'imones-informacija', label: 'Įmonės informacija', footerGated: true },
    { slug: 'atsakomybes-apribojimas', label: 'Atsakomybės apribojimas', footerGated: true },
    { slug: 'kontaktai', label: 'Kontaktai', role: 'contact' },
    { slug: 'apie-aposmart', label: 'Apie Aposmart' },
    { slug: 'pranesti-apie-klaida', label: 'Pranešti apie klaidą' },
  ],
  PL: [
    { slug: 'polityka-prywatnosci', label: 'Polityka prywatności', role: 'privacy' },
    { slug: 'polityka-plikow-cookie', label: 'Polityka plików cookie' },
    { slug: 'regulamin', label: 'Regulamin' },
    { slug: 'dane-firmy', label: 'Dane firmy', footerGated: true },
    {
      slug: 'wylaczenie-odpowiedzialnosci',
      label: 'Wyłączenie odpowiedzialności',
      footerGated: true,
    },
    { slug: 'kontakt-z-nami', label: 'Kontakt', role: 'contact' },
    { slug: 'o-aposmart', label: 'O Aposmart' },
    { slug: 'zglos-blad', label: 'Zgłoś błąd' },
  ],
  ES: [
    { slug: 'politica-de-privacidad', label: 'Política de privacidad', role: 'privacy' },
    { slug: 'politica-de-cookies', label: 'Política de cookies' },
    { slug: 'condiciones-de-uso', label: 'Condiciones de uso' },
    { slug: 'datos-de-la-empresa', label: 'Datos de la empresa', footerGated: true },
    {
      slug: 'exencion-de-responsabilidad',
      label: 'Exención de responsabilidad',
      footerGated: true,
    },
    { slug: 'contacto', label: 'Contacto', role: 'contact' },
    { slug: 'sobre-aposmart', label: 'Sobre Aposmart' },
    { slug: 'informar-de-un-error', label: 'Informar de un error' },
  ],
  LV: [
    { slug: 'privatuma-politika', label: 'Privātuma politika', role: 'privacy' },
    { slug: 'sikdatnu-politika', label: 'Sīkdatņu politika' },
    { slug: 'lietosanas-noteikumi', label: 'Lietošanas noteikumi' },
    { slug: 'uznemuma-informacija', label: 'Uzņēmuma informācija', footerGated: true },
    { slug: 'atruna', label: 'Atruna', footerGated: true },
    { slug: 'kontakti', label: 'Kontakti', role: 'contact' },
    { slug: 'par-aposmart', label: 'Par Aposmart' },
    { slug: 'zinot-par-kludu', label: 'Ziņot par kļūdu' },
  ],
  IT: [
    { slug: 'informativa-sulla-privacy', label: 'Informativa sulla privacy', role: 'privacy' },
    { slug: 'politica-sui-cookie', label: 'Politica sui cookie' },
    { slug: 'condizioni-di-utilizzo', label: 'Condizioni di utilizzo' },
    { slug: 'dati-aziendali', label: 'Dati aziendali', footerGated: true },
    {
      slug: 'esclusione-di-responsabilita',
      label: 'Esclusione di responsabilità',
      footerGated: true,
    },
    { slug: 'contatti', label: 'Contatti', role: 'contact' },
    { slug: 'su-aposmart', label: 'Su Aposmart' },
    { slug: 'segnala-un-errore', label: 'Segnala un errore' },
  ],
  RO: [
    {
      slug: 'politica-de-confidentialitate',
      label: 'Politica de confidențialitate',
      role: 'privacy',
    },
    { slug: 'politica-cookie', label: 'Politica de cookie-uri' },
    { slug: 'termeni-de-utilizare', label: 'Termeni de utilizare' },
    { slug: 'date-despre-companie', label: 'Date despre companie', footerGated: true },
    {
      slug: 'declinarea-responsabilitatii',
      label: 'Declinarea responsabilității',
      footerGated: true,
    },
    { slug: 'contact', label: 'Contact', role: 'contact' },
    { slug: 'despre-aposmart', label: 'Despre Aposmart' },
    { slug: 'raporteaza-o-eroare', label: 'Raportează o eroare' },
  ],
};

// Markets whose identity and disclaimer articles are authored in the CMS. Only
// those markets link the pair from their footer; everywhere else the pages stay
// reachable by direct URL and simply go unlinked. Add a market here once its two
// articles are live.
const LEGAL_FOOTER_GATE_OPEN: ReadonlySet<Country> = new Set<Country>(['DE']);

/**
 * Whether this market links its identity and disclaimer pages from the footer.
 * SE reads it too: its full footer links the pair inline rather than through
 * LEGAL_ARTICLES, so one switch has to cover both footer variants.
 */
export function legalFooterGateOpen(country: Country): boolean {
  return LEGAL_FOOTER_GATE_OPEN.has(country);
}

function legalLinksFor(country: Exclude<Country, 'SE'>): readonly FooterLink[] {
  const gateOpen = legalFooterGateOpen(country);
  return LEGAL_ARTICLES[country]
    .filter(({ footerGated }) => gateOpen || !footerGated)
    .map(({ slug, label }) => ({
      label,
      path: `/article/${slug}`,
    }));
}

/**
 * Article slugs that resolve on the given market. Empty for SE, which is not
 * slug-restricted at all -- callers must treat SE as "everything allowed" rather
 * than reading an empty set as "nothing allowed".
 */
export function legalArticleSlugs(country: Country): ReadonlySet<string> {
  if (country === 'SE') return new Set();
  return new Set(LEGAL_ARTICLES[country].map((a) => a.slug));
}

// SE has no LEGAL_ARTICLES entry (it resolves every slug and keeps the full
// footer), so its two role-bearing articles are named here. Both are live and
// listed in sitemap.xml.
const SE_ROLE_SLUGS: Readonly<Record<ArticleRole, string>> = {
  privacy: 'integritetspolicy',
  contact: 'kontakta-oss',
};

/**
 * The slug of this market's privacy policy or contact article. Every market has
 * both, so this always resolves; the throw guards against a future market being
 * added to LEGAL_ARTICLES without its roles marked, which would otherwise reach
 * visitors as a dead link instead of failing loudly on the first render.
 */
export function articleSlugForRole(country: Country, role: ArticleRole): string {
  if (country === 'SE') return SE_ROLE_SLUGS[role];

  const match = LEGAL_ARTICLES[country].find((article) => article.role === role);
  if (!match) {
    throw new Error(`No '${role}' article configured for ${country}`);
  }
  return match.slug;
}

export const COUNTRY_CONFIG: Readonly<Record<Country, CountryConfig>> = {
  SE: {
    locale: 'sv',
    currency: 'SEK',
    timezone: 'Europe/Stockholm',
    countryName: 'Sweden',
    productTable: 'medpdb2',
    cacheTable: 'portal_cache',
    typesenseCollection: 'medpdb',
    carouselView: 'carousel_view',
    host: 'https://www.aposmart.se/',
    // Unchanged: already indexed and listed in sitemap.xml.
    campaignsPath: 'article/alla-rabatter-och-kampanjer',
    consentMode: 'self-hosted',
    features: ALL_ON,
    footer: { variant: 'full', legalLinks: [] },
    siteSchema: {
      organizationName: 'Aposmart.se',
      areaServed: 'SE',
      sameAs: [
        'https://www.facebook.com/aposmart.se',
        'https://www.instagram.com/aposmart.se/',
        'https://www.linkedin.com/company/aposmart-se/',
      ],
    },
    curatedMeta: true,
    featuredArticleSlugs: [
      'momssankning-kosttillskott-2026',
      'e-barometern-2025-apoteksbranschen-leder-svensk-e-handel',
      'blodprov-fore-kosttillskott',
      'augusti-2025-prisrapport-visar-kraftiga-prishojningar-pa-allergilakemedel',
      'omega-3',
      'apotek-pa-natet-online-fordelar',
    ],
    priceGuideSlugs: [
      'prisjamforelse-mounjaro-wegovy-ozempic-saxenda',
      'allergilakemedel-pollensasong-prisjamforelse',
      'hogkostnadsskyddet-2026-prisguide-formanen',
      'fran-receptfritt-till-receptbelagt-aknebehandling-kostnad',
      'prisguide-for-haravfall-minoxidil-finasterid-prp-hartransplantation',
      'fungoral-schampo-pris-kopa-billigast',
      'nocco-flak-prisjamforelse-billigaste-pris',
      'reducerad-moms-kosttillskott-pris-fore-efter',
      'prisjamforelser-viktnedgang-ayd-yazen-fit-for-livet-vikto-velora',
      'pris-for-potenslakemedel-jamforelse-viagra-cialis',
      'parallellimport-billigare-atrimusrx-ebb-medical',
    ],
    inStore: {
      stockVendors: ['life', 'doz', 'kronan', 'apohja', 'kicks', 'lyko', 'apoteket'],
      topCities,
      locations,
    },
  },
  DE: {
    locale: 'de',
    currency: 'EUR',
    timezone: 'Europe/Berlin',
    countryName: 'Germany',
    productTable: 'medpdb2_de',
    cacheTable: 'portal_cache_de',
    typesenseCollection: 'medpdb_de',
    carouselView: 'carousel_view_de',
    // OD0: exact ccTLD host confirmed at deploy time (issue #13).
    host: 'https://www.aposmart.de/',
    campaignsPath: 'rabatte-und-aktionen',
    consentMode: 'self-hosted',
    features: ALL_OFF,
    footer: { variant: 'limited', legalLinks: legalLinksFor('DE') },
    siteSchema: {
      organizationName: 'Aposmart.de',
      areaServed: 'DE',
      sameAs: [],
    },
    curatedMeta: false,
    featuredArticleSlugs: [],
    priceGuideSlugs: [],
  },
  NO: {
    locale: 'nb',
    currency: 'NOK',
    timezone: 'Europe/Oslo',
    countryName: 'Norway',
    productTable: 'medpdb2_no',
    cacheTable: 'portal_cache_no',
    typesenseCollection: 'medpdb_no',
    carouselView: 'carousel_view_no',
    host: 'https://www.aposmart.no/',
    campaignsPath: 'alle-rabatter-og-kampanjer',
    consentMode: 'self-hosted',
    features: ALL_OFF,
    footer: { variant: 'limited', legalLinks: legalLinksFor('NO') },
    siteSchema: {
      organizationName: 'Aposmart.no',
      areaServed: 'NO',
      sameAs: [],
    },
    curatedMeta: false,
    featuredArticleSlugs: [],
    priceGuideSlugs: [],
  },
  DK: {
    locale: 'da',
    currency: 'DKK',
    timezone: 'Europe/Copenhagen',
    countryName: 'Denmark',
    productTable: 'medpdb2_dk',
    cacheTable: 'portal_cache_dk',
    typesenseCollection: 'medpdb_dk',
    carouselView: 'carousel_view_dk',
    host: 'https://www.aposmart.dk/',
    campaignsPath: 'alle-rabatter-og-kampagner',
    consentMode: 'self-hosted',
    features: ALL_OFF,
    footer: { variant: 'limited', legalLinks: legalLinksFor('DK') },
    siteSchema: {
      organizationName: 'Aposmart.dk',
      areaServed: 'DK',
      sameAs: [],
    },
    curatedMeta: false,
    featuredArticleSlugs: [],
    priceGuideSlugs: [],
  },
  EE: {
    locale: 'et',
    currency: 'EUR',
    timezone: 'Europe/Tallinn',
    countryName: 'Estonia',
    productTable: 'medpdb2_ee',
    cacheTable: 'portal_cache_ee',
    typesenseCollection: 'medpdb_ee',
    carouselView: 'carousel_view_ee',
    host: 'https://www.aposmart.ee/',
    campaignsPath: 'soodustused-ja-kampaaniad',
    consentMode: 'self-hosted',
    features: ALL_OFF,
    footer: { variant: 'limited', legalLinks: legalLinksFor('EE') },
    siteSchema: {
      organizationName: 'Aposmart.ee',
      areaServed: 'EE',
      sameAs: [],
    },
    curatedMeta: false,
    featuredArticleSlugs: [],
    priceGuideSlugs: [],
  },
  GB: {
    locale: 'en-GB',
    currency: 'GBP',
    timezone: 'Europe/London',
    countryName: 'United Kingdom',
    productTable: 'medpdb2_gb',
    cacheTable: 'portal_cache_gb',
    typesenseCollection: 'medpdb_gb',
    carouselView: 'carousel_view_gb',
    host: 'https://www.aposmart.co.uk/',
    campaignsPath: 'all-discounts-and-deals',
    consentMode: 'self-hosted',
    features: ALL_OFF,
    footer: { variant: 'limited', legalLinks: legalLinksFor('GB') },
    siteSchema: {
      organizationName: 'Aposmart.co.uk',
      areaServed: 'GB',
      sameAs: [],
    },
    curatedMeta: false,
    featuredArticleSlugs: [],
    priceGuideSlugs: [],
  },
  FI: {
    locale: 'fi',
    currency: 'EUR',
    timezone: 'Europe/Helsinki',
    countryName: 'Finland',
    productTable: 'medpdb2_fi',
    cacheTable: 'portal_cache_fi',
    typesenseCollection: 'medpdb_fi',
    carouselView: 'carousel_view_fi',
    host: 'https://www.aposmart.fi/',
    campaignsPath: 'kaikki-alennukset-ja-kampanjat',
    consentMode: 'self-hosted',
    features: ALL_OFF,
    footer: { variant: 'limited', legalLinks: legalLinksFor('FI') },
    siteSchema: {
      organizationName: 'Aposmart.fi',
      areaServed: 'FI',
      sameAs: [],
    },
    curatedMeta: false,
    featuredArticleSlugs: [],
    priceGuideSlugs: [],
  },
  LT: {
    locale: 'lt',
    currency: 'EUR',
    timezone: 'Europe/Vilnius',
    countryName: 'Lithuania',
    productTable: 'medpdb2_lt',
    cacheTable: 'portal_cache_lt',
    typesenseCollection: 'medpdb_lt',
    carouselView: 'carousel_view_lt',
    host: 'https://www.aposmart.lt/',
    campaignsPath: 'nuolaidos-ir-akcijos',
    consentMode: 'self-hosted',
    features: ALL_OFF,
    footer: { variant: 'limited', legalLinks: legalLinksFor('LT') },
    siteSchema: {
      organizationName: 'Aposmart.lt',
      areaServed: 'LT',
      sameAs: [],
    },
    curatedMeta: false,
    featuredArticleSlugs: [],
    priceGuideSlugs: [],
  },
  PL: {
    locale: 'pl',
    currency: 'PLN',
    timezone: 'Europe/Warsaw',
    countryName: 'Poland',
    productTable: 'medpdb2_pl',
    cacheTable: 'portal_cache_pl',
    typesenseCollection: 'medpdb_pl',
    carouselView: 'carousel_view_pl',
    host: 'https://www.aposmart.pl/',
    campaignsPath: 'wszystkie-rabaty-i-promocje',
    consentMode: 'self-hosted',
    features: ALL_OFF,
    footer: { variant: 'limited', legalLinks: legalLinksFor('PL') },
    siteSchema: {
      organizationName: 'Aposmart.pl',
      areaServed: 'PL',
      sameAs: [],
    },
    curatedMeta: false,
    featuredArticleSlugs: [],
    priceGuideSlugs: [],
  },
  ES: {
    locale: 'es',
    currency: 'EUR',
    timezone: 'Europe/Madrid',
    countryName: 'Spain',
    productTable: 'medpdb2_es',
    cacheTable: 'portal_cache_es',
    typesenseCollection: 'medpdb_es',
    carouselView: 'carousel_view_es',
    host: 'https://www.aposmart.es/',
    campaignsPath: 'todos-los-descuentos-y-promociones',
    consentMode: 'self-hosted',
    features: ALL_OFF,
    footer: { variant: 'limited', legalLinks: legalLinksFor('ES') },
    siteSchema: {
      organizationName: 'Aposmart.es',
      areaServed: 'ES',
      sameAs: [],
    },
    curatedMeta: false,
    featuredArticleSlugs: [],
    priceGuideSlugs: [],
  },
  LV: {
    locale: 'lv',
    currency: 'EUR',
    timezone: 'Europe/Riga',
    countryName: 'Latvia',
    productTable: 'medpdb2_lv',
    cacheTable: 'portal_cache_lv',
    typesenseCollection: 'medpdb_lv',
    carouselView: 'carousel_view_lv',
    host: 'https://www.aposmart.lv/',
    campaignsPath: 'visas-atlaides-un-akcijas',
    consentMode: 'self-hosted',
    features: ALL_OFF,
    footer: { variant: 'limited', legalLinks: legalLinksFor('LV') },
    siteSchema: {
      organizationName: 'Aposmart.lv',
      areaServed: 'LV',
      sameAs: [],
    },
    curatedMeta: false,
    featuredArticleSlugs: [],
    priceGuideSlugs: [],
  },
  IT: {
    locale: 'it',
    currency: 'EUR',
    timezone: 'Europe/Rome',
    countryName: 'Italy',
    productTable: 'medpdb2_it',
    cacheTable: 'portal_cache_it',
    typesenseCollection: 'medpdb_it',
    carouselView: 'carousel_view_it',
    host: 'https://www.aposmart.it/',
    campaignsPath: 'tutti-gli-sconti-e-le-promozioni',
    consentMode: 'self-hosted',
    features: ALL_OFF,
    footer: { variant: 'limited', legalLinks: legalLinksFor('IT') },
    siteSchema: {
      organizationName: 'Aposmart.it',
      areaServed: 'IT',
      sameAs: [],
    },
    curatedMeta: false,
    featuredArticleSlugs: [],
    priceGuideSlugs: [],
  },
  RO: {
    locale: 'ro',
    currency: 'RON',
    timezone: 'Europe/Bucharest',
    countryName: 'Romania',
    productTable: 'medpdb2_ro',
    cacheTable: 'portal_cache_ro',
    typesenseCollection: 'medpdb_ro',
    carouselView: 'carousel_view_ro',
    host: 'https://www.aposmart.ro/',
    campaignsPath: 'toate-reducerile-si-promotiile',
    consentMode: 'self-hosted',
    features: ALL_OFF,
    footer: { variant: 'limited', legalLinks: legalLinksFor('RO') },
    siteSchema: {
      organizationName: 'Aposmart.ro',
      areaServed: 'RO',
      sameAs: [],
    },
    curatedMeta: false,
    featuredArticleSlugs: [],
    priceGuideSlugs: [],
  },
};

// Markets offered in the footer's market switcher, in display order. Only markets
// whose site is actually live are listed: a market can be registered in
// COUNTRY_CONFIG long before its ccTLD resolves, and a flag linking to a host that
// does not answer is a dead link. marketSwitcherLinks drops whichever live market
// the visitor is already on, so each site shows all the other live ones. Hosts come
// from COUNTRY_CONFIG so a ccTLD is never written twice.
//
// Labels are endonyms hardcoded per market rather than i18n messages: the point
// of the row is to name the destination country, and a label translated into the
// visitor's language ("Schweden") would say less about where the link goes than
// the country's own name for itself.
const MARKET_SWITCHER_ORDER: readonly { readonly country: Country; readonly label: string }[] = [
  { country: 'SE', label: 'Sverige' },
  { country: 'NO', label: 'Norge' },
  { country: 'DK', label: 'Danmark' },
  { country: 'DE', label: 'Deutschland' },
  { country: 'GB', label: 'United Kingdom' },
  { country: 'EE', label: 'Eesti' },
  { country: 'FI', label: 'Suomi' },
  { country: 'LT', label: 'Lietuva' },
  { country: 'PL', label: 'Polska' },
];

/** The other live markets a visitor on `country` can switch to. Never includes `country` itself. */
export function marketSwitcherLinks(country: Country): readonly MarketSwitchLink[] {
  return MARKET_SWITCHER_ORDER.filter((market) => market.country !== country).map((market) => ({
    label: market.label,
    href: COUNTRY_CONFIG[market.country].host,
    flagSrc: `assets/flags/4x3/${market.country.toLowerCase()}.svg`,
  }));
}

// The build-time Country constant. Overridable in tests; in a build it resolves
// from the (file-replaced) environment so it stays paired with the build's locale.
export const CURRENT_COUNTRY = new InjectionToken<Country>('CURRENT_COUNTRY', {
  providedIn: 'root',
  factory: (): Country => environment.country,
});

export function isFeatureEnabled(country: Country, feature: FeatureFlag): boolean {
  return COUNTRY_CONFIG[country].features[feature];
}
